import { fetchConfig } from "../base/global";
/**
 * Advanced Options Card Web Component
 * Handles quantity selection, unit switching, and cart functionality
 */
class AdvancedOptionsCard extends HTMLElement {
  constructor() {
    super();
    this.card = this;
    this.quantityInput = this.querySelector(
      ".advanced-options-card__quantity-input",
    );
    this.quantityButtons = this.querySelectorAll(
      ".advanced-options-card__quantity-btn",
    );
    this.unitSelector = this.querySelector(
      ".advanced-options-card__unit-select",
    );
    this.buyNowButton = this.querySelector(".advanced-options-card__buy-btn");
    this.casesCount = this.querySelector('[id*="cases-count"]');
    this.totalPrice = this.querySelector('[id*="total-price"]');
    this.ignoreCartNotification = this.querySelector(
      "[data-ignore-cart-notification]",
    );

    this.productId = this.buyNowButton?.dataset.productId;
    this.variantId = this.buyNowButton?.dataset.variantId;
    this.cartItemKey = null; // Will be set when cart item is added
    this.unitPrice = 0;
    this.casePrice = 0;
    this.quantity = 1;
    this.isCase = true;

    this.init();
  }

  /**
   * Initialize the component.
   */
  init() {
    this.bindEvents();
    this.calculatePrices();
  }

  /**
   * Bind event listeners to the component elements.
   */
  bindEvents() {
    // Quantity selector events
    this.quantityButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        const action = e.currentTarget.dataset.action;
        this.updateQuantity(action);
      });
    });

    this.quantityInput?.addEventListener("input", (e) => {
      const rawValue = e.target.value;
      // Allow empty string while typing, but default to 1 for calculation
      const inputValue = rawValue === "" ? 1 : parseInt(rawValue) || 1;
      const min = parseInt(e.target.min) || 1;
      const max = parseInt(e.target.max) || 999;

      // Clamp value between min and max
      const clampedValue = Math.max(min, Math.min(max, inputValue));
      this.quantity = clampedValue;

      // Only update input if value was clamped or if we need to format it
      // Don't interfere with user typing unless value exceeds max
      if (inputValue > max || inputValue < min) {
        e.target.value = clampedValue.toString().padStart(2, "0");
      }

      this.updateDisplay();
    });

    // Also validate on blur to ensure value is correct when user leaves the field
    this.quantityInput?.addEventListener("blur", (e) => {
      const inputValue = parseInt(e.target.value) || 1;
      const min = parseInt(e.target.min) || 1;
      const max = parseInt(e.target.max) || 999;

      // Clamp value between min and max
      this.quantity = Math.max(min, Math.min(max, inputValue));
      e.target.value = this.quantity.toString().padStart(2, "0");
      this.updateDisplay();
    });

    // Unit selector events
    this.unitSelector?.addEventListener("change", (e) => {
      this.isCase = e.target.value === "case";
      this.updateDisplay();
    });

    // Buy now button
    this.buyNowButton?.addEventListener("click", () => {
      this.addToCart();
    });
  }

  /**
   * Update quantity based on action.
   * @param {string} action - The action to perform ('increase' or 'decrease').
   */
  updateQuantity(action) {
    if (action === "increase") {
      this.quantity = Math.min(this.quantity + 1, 999);
    } else if (action === "decrease") {
      this.quantity = Math.max(this.quantity - 1, 1);
    }

    if (this.quantityInput) {
      this.quantityInput.value = this.quantity.toString().padStart(2, "0");
    }
    this.updateDisplay();
  }

  /**
   * Update the display with current values.
   */
  updateDisplay() {
    if (this.casesCount) {
      this.casesCount.textContent = this.quantity;
    }

    if (this.totalPrice) {
      const totalPrice = this.isCase
        ? this.casePrice * this.quantity
        : this.unitPrice * this.quantity;

      this.totalPrice.textContent = this.formatMoney(totalPrice);
    }
  }

  /**
   * Calculate prices from data attributes and table element.
   */
  calculatePrices() {
    // Try to get case price from table element first
    const priceElement = this.querySelector(
      ".advanced-options-card__table-cell--case-price",
    );
    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^\d.,]/g, "");
      this.casePrice = parseFloat(priceText.replace(",", ".")) || 0;
    } else {
      // Fallback to data attribute
      const casePriceData = this.dataset.casePrice;
      if (casePriceData) {
        this.casePrice = parseFloat(casePriceData) || 0;
      }
    }

    // Get case quantity from metafield or default to 12
    const caseQuantity = this.getCaseQuantity();
    this.unitPrice = this.casePrice / caseQuantity; // Unit price = case price / case quantity
  }

  /**
   * Gets the case quantity from the product metafield.
   * @returns {number} The case quantity from metafield or default 12.
   */
  getCaseQuantity() {
    // Try to get case quantity from metafield data attribute
    const metafieldData = this.dataset.caseQuantity;
    if (metafieldData) {
      return parseInt(metafieldData) || 12;
    }

    // Fallback to default case quantity
    return 12;
  }

  /**
   * Format money amount for display.
   * @param {number} amount - The amount to format.
   * @returns {string} The formatted money string.
   */
  formatMoney(amount) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  }

  /**
   * Adds items to the cart with variant properties.
   * Reads variant data from data attributes and includes them as cart item properties.
   */
  addToCart() {
    if (!this.productId || !this.variantId) {
      console.error("Product ID or Variant ID not found");
      return;
    }

    this.buyNowButton.disabled = true;

    // Get variant data from data attributes.
    const variantBarcode = this.dataset.variantBarcode;
    const variantSku = this.dataset.variantSku;
    const variantCaseQty =
      this.dataset.variantCaseQty && this.dataset.variantCaseQty !== "N/A"
        ? parseInt(this.dataset.variantCaseQty).toFixed(0)
        : false;
    const variantUnitPrice =
      this.dataset.variantUnitPrice && this.dataset.variantUnitPrice !== "N/A"
        ? parseFloat(this.dataset.variantUnitPrice)
        : false;


    // Build properties object conditionally, only including values that exist.
    const properties = {};
    const translations = window.advancedOptionsCardTranslations || {};

    if (variantBarcode && translations.barcode) {
      properties[translations.barcode] = variantBarcode;
    }
    if (variantSku && translations.code) {
      properties[translations.code] = variantSku;
    }
    if (variantCaseQty && translations.caseQty) {
      properties[translations.caseQty] = variantCaseQty;
    }
    if (variantUnitPrice && translations.unitPrice) {
      properties[translations.unitPrice] =
        [window.advancedOptionsCardTranslations.currencySymbol] +
        "" +
        variantUnitPrice;
    }

    const formData = {
      items: [
        {
          id: parseInt(this.variantId),
          quantity: this.quantity,
          ...(Object.keys(properties).length > 0 && { properties }),
        },
      ],
    };

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then(async (result) => {
        // Set cart item key for section updates.
        // The /cart/add.js response returns items array, so extract key from first item.
        this.cartItemKey = result.items?.[0]?.key || result.key;

        // Show success message
        this.showSuccessMessage();

        // Get cart notification element to trigger it.
        const cartNotification = document.querySelector("cart-notification");

        // Fetch updated cart data with sections
        const sectionsToRender = this.getSectionsToRender();
        const body = JSON.stringify({
          sections: sectionsToRender.map(({ section }) => section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);

            // If cart notification exists, use its renderContents method to open it.
            if (
              !this.ignoreCartNotification &&
              cartNotification &&
              typeof cartNotification.renderContents === "function"
            ) {
              // Set the key property that cart-notification expects.
              parsedState.key = this.cartItemKey;
              try {
                cartNotification.renderContents(parsedState);
              } catch (error) {
                console.error(
                  "Error calling cart notification renderContents:",
                  error,
                );
                // Fallback to manual rendering
                this.renderSections(parsedState);
              }
            } else {
              // Fallback to manual section rendering.
              this.renderSections(parsedState);
            }

            // Use PUB_SUB_EVENTS to trigger cart updates
            if (
              typeof publish !== "undefined" &&
              typeof PUB_SUB_EVENTS !== "undefined"
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "advanced-options-card",
                cartData: parsedState,
                variantId: this.variantId,
              });
            }
          })
          .catch((error) => {
            console.error("Error updating cart sections:", error);
            // Fallback: manually update cart count
            this.updateCartCountManually();
          });

        // Reset form after a short delay
        setTimeout(() => {
          this.resetForm();
        }, 1500);
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        this.showErrorMessage();
      })
      .finally(() => {
        this.buyNowButton.disabled = false;
        this.buyNowButton.textContent = "Buy Now";
      });
  }

  /**
   * Show success message.
   */
  showSuccessMessage() {
    const originalText = this.buyNowButton.textContent;
    this.buyNowButton.textContent = "Added to Cart!";
    this.buyNowButton.style.background = "#28a745";

    setTimeout(() => {
      this.buyNowButton.textContent = originalText;
      this.buyNowButton.style.background = "";
    }, 2000);
  }

  /**
   * Show error message.
   */
  showErrorMessage() {
    const originalText = this.buyNowButton.textContent;
    this.buyNowButton.textContent = "Error - Try Again";
    this.buyNowButton.style.background = "#dc3545";

    setTimeout(() => {
      this.buyNowButton.textContent = originalText;
      this.buyNowButton.style.background = "";
    }, 3000);
  }

  /**
   * Get sections to render for cart updates.
   * @returns {Array} Array of section objects.
   */
  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        section: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey || "default"}"]`,
      },
      {
        id: "cart-notification-button",
        section: "cart-notification-button",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: "#shopify-section-cart-icon-bubble",
      },
    ];
  }

  /**
   * Render sections using the same pattern as quick-order-list.js.
   * @param {Object} parsedState - The parsed state from cart update.
   */
  renderSections(parsedState) {
    const { sections } = parsedState;

    this.getSectionsToRender().forEach(({ id, selector, section }) => {
      const sectionElement = document.getElementById(id);
      if (!sectionElement) return;

      const parsedHTML = new DOMParser().parseFromString(
        sections[section],
        "text/html",
      );
      const newSection = parsedHTML.querySelector(selector);

      if (newSection) {
        sectionElement.innerHTML = newSection.innerHTML;
      }
    });
  }

  /**
   * Manual fallback to update cart count.
   */
  updateCartCountManually() {
    const cartCount = document.querySelector(".cart-count-bubble");
    if (cartCount) {
      const currentCount = parseInt(cartCount.textContent) || 0;
      cartCount.textContent = currentCount + this.quantity;
    }
  }

  /**
   * Reset form to initial state.
   */
  resetForm() {
    this.quantity = 1;
    if (this.quantityInput) {
      this.quantityInput.value = "01";
    }
    if (this.unitSelector) {
      this.unitSelector.value = "case";
    }
    this.isCase = true;
    this.updateDisplay();
  }
}

// Register the custom element
customElements.define("advanced-options-card", AdvancedOptionsCard);
